/**
 * Alloi custom Selling Plans buy box.
 *
 * A vanilla custom element (no theme imports) that turns the server-rendered
 * `Alloi_buy-box.liquid` markup into an interactive purchase-option selector.
 *
 * How it plugs into the theme (see Custom-Buy-Box-Research.md):
 *  - The theme's add-to-cart submits `new FormData(form)` (assets/product-form.js).
 *    So for the selected plan to reach the cart, a hidden `selling_plan` input
 *    must live INSIDE that <form>. This class injects + keeps one in sync, so
 *    the native Add to cart button — and the sticky bar that "puppets" it —
 *    carry the plan with zero changes to product-form.js.
 *  - Variant changes are read from the bubbling `variant:update` DOM event
 *    (events.js → ThemeEvents.variantUpdate). All variants' plan data is
 *    pre-rendered in the markup, so switching plans needs NO API call.
 *
 * `addToCart()` is a clean convenience method per the brief, but it is a
 * SECONDARY path — the primary flow stays the theme's own form submit so the
 * cart drawer, cart events and sticky bar keep working.
 */
class AlloiBuyBox extends HTMLElement {
  constructor() {
    super();
    /** @type {string|null} The selected selling plan ID ("" / null = one-time). */
    this.selectedPlanId = null;
    /** @type {number} Bottle count for the selected option (bundles ship 2 or 3). */
    this.selectedQuantity = 1;
    /** @type {HTMLElement|null} The currently visible per-variant group. */
    this.activeGroup = null;
    /** @type {boolean} True when the selected option is a pre-order (sold-out variant). */
    this.isPreorder = false;
    /** @type {string} Add-to-cart label used in pre-order mode. */
    this.preorderLabel = 'Pre-order';
    this.handleOptionClick = this.handleOptionClick.bind(this);
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    this.toggleCollapsed = this.toggleCollapsed.bind(this);
  }

  connectedCallback() {
    this.sectionEl = this.closest('.shopify-section') || document;
    this.localInput = this.querySelector('.alloi-buy-box__selected-plan');

    // Bridge the card animation choice onto the section so the CTA buttons
    // (which live outside this element) can match it via CSS.
    if (this.sectionEl instanceof HTMLElement) {
      this.sectionEl.dataset.alloiCtaAnim = this.dataset.anim || 'none';
    }

    // Pre-order button label, with the {eta} token filled from the metafield.
    const rawLabel = this.dataset.preorderLabel || 'Pre-order';
    const eta = (this.dataset.preorderEta || '').trim();
    this.preorderLabel = rawLabel.replace('{eta}', eta).replace(/\s{2,}/g, ' ').trim();

    // Relocate the pre-order panel (ETA + FAQ) to sit under the add-to-cart button.
    this.#relocatePreorderPanel();

    this.activeGroup = this.querySelector('.alloi-buy-box__group:not([hidden])') ||
      this.querySelector('.alloi-buy-box__group');

    this.bindGroup(this.activeGroup);
    this.bindCollapseToggle();

    // React to the theme's variant change event (bubbles up through the section).
    this.sectionEl.addEventListener('variant:update', this.handleVariantUpdate);

    // Select a sensible default so the form input matches the visible state.
    this.selectDefault(this.activeGroup);
  }

  disconnectedCallback() {
    if (this.sectionEl) {
      this.sectionEl.removeEventListener('variant:update', this.handleVariantUpdate);
    }
    // The panel was relocated outside this element, so remove it explicitly on
    // teardown (theme-editor re-render) to avoid orphaned duplicates.
    if (this.preorderPanel) this.preorderPanel.remove();
  }

  /* ----------------------------------------------------------------------- *
   * Option binding + selection
   * ----------------------------------------------------------------------- */

  /**
   * Attach click handlers to every option button in a group.
   * @param {HTMLElement|null} group
   */
  bindGroup(group) {
    if (!group) return;
    const options = group.querySelectorAll('.alloi-buy-box__option');
    options.forEach((option) => {
      option.removeEventListener('click', this.handleOptionClick);
      option.addEventListener('click', this.handleOptionClick);
    });
  }

  /**
   * @param {MouseEvent} event
   */
  handleOptionClick(event) {
    const option = /** @type {HTMLElement} */ (event.currentTarget);
    this.selectOption(option);
  }

  /**
   * Pick a default option for a group: first subscription when the product
   * requires a selling plan, otherwise the first option (one-time).
   * @param {HTMLElement|null} group
   */
  selectDefault(group) {
    if (!group) return;
    const requiresPlan = this.dataset.requiresSellingPlan === 'true';
    let target = null;
    if (requiresPlan) {
      target = group.querySelector('.alloi-buy-box__option[data-type="subscription"]');
    }
    target = target || group.querySelector('.alloi-buy-box__option');
    if (target) this.selectOption(target);
  }

  /**
   * Mark an option selected and propagate the plan to the form.
   * @param {HTMLElement} option
   */
  selectOption(option) {
    const group = option.closest('.alloi-buy-box__group') || this.activeGroup;
    if (!group) return;

    group.querySelectorAll('.alloi-buy-box__option').forEach((el) => {
      const selected = el === option;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });

    const planId = option.dataset.sellingPlanId || '';
    this.selectedQuantity = parseInt(option.dataset.quantity, 10) || 1;
    this.isPreorder = option.dataset.type === 'preorder';
    this.syncQuantityInputs(this.selectedQuantity);
    this.selectPlan(planId);
    this.applyButtonState(this.isPreorder);
  }

  /**
   * Public: set the active selling plan ID and sync every selling_plan input.
   * @param {string|number|null} planId
   */
  selectPlan(planId) {
    this.selectedPlanId = planId ? String(planId) : '';
    this.syncSellingPlanInputs(this.selectedPlanId);
    this.updatePriceDisplay();

    this.dispatchEvent(
      new CustomEvent('alloi:buybox:change', {
        bubbles: true,
        detail: { sellingPlanId: this.selectedPlanId || null },
      })
    );
  }

  /* ----------------------------------------------------------------------- *
   * Form integration
   * ----------------------------------------------------------------------- */

  /**
   * The host product form, located by id (data-form-id). May be null if the
   * buy box is rendered without a form on the page.
   * @returns {HTMLFormElement|null}
   */
  getProductForm() {
    const formId = this.dataset.formId;
    if (!formId) return null;
    const el = document.getElementById(formId);
    return el instanceof HTMLFormElement ? el : null;
  }

  /**
   * Ensure a hidden `selling_plan` input exists inside the product form so the
   * theme's native FormData submit includes it. Returns the input (or null).
   * @returns {HTMLInputElement|null}
   */
  ensureFormInput() {
    const form = this.getProductForm();
    if (!form) return null;

    let input = /** @type {HTMLInputElement|null} */ (form.querySelector('input[name="selling_plan"]'));
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'selling_plan';
      input.dataset.alloiBuyBox = 'true';
      form.appendChild(input);
    }
    return input;
  }

  /**
   * Write the plan ID to the form input, the local source-of-truth input, and
   * any other selling_plan inputs present (covers all `[name="selling_plan"]`).
   * @param {string} planId
   */
  syncSellingPlanInputs(planId) {
    const value = planId || '';

    const formInput = this.ensureFormInput();
    if (formInput) formInput.value = value;

    if (this.localInput) this.localInput.value = value;

    document.querySelectorAll('input[name="selling_plan"]').forEach((input) => {
      if (input instanceof HTMLInputElement) input.value = value;
    });
  }

  /**
   * Drive the product form's quantity input so the native add-to-cart submits
   * the bundle's bottle count (2-bottle / 3-bottle plans ship 2 / 3). The visible
   * stepper is hidden via CSS, so this is the single source of quantity truth.
   * @param {number} quantity
   */
  syncQuantityInputs(quantity) {
    const value = String(quantity > 0 ? quantity : 1);
    const form = this.getProductForm();
    if (!form) return;

    const input = form.querySelector('input[name="quantity"]');
    if (!(input instanceof HTMLInputElement)) return;

    if (input.value !== value) {
      input.value = value;
      // Notify the quantity-selector component and any price/cart listeners.
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Collect the add-to-cart buttons we may need to re-enable for pre-order:
   * the main product-form button(s) plus the sticky bar button (which puppets it).
   * @returns {HTMLButtonElement[]}
   */
  getAddToCartButtons() {
    const buttons = [];
    const form = this.getProductForm();
    if (form) {
      form.querySelectorAll('.add-to-cart-button, button[name="add"]').forEach((b) => {
        if (b instanceof HTMLButtonElement) buttons.push(b);
      });
    }
    document.querySelectorAll('.sticky-add-to-cart__button').forEach((b) => {
      if (b instanceof HTMLButtonElement) buttons.push(b);
    });
    return buttons;
  }

  /**
   * Pre-order mode: the variant is sold out, so the theme renders the add-to-cart
   * button disabled / "Sold out". The pre-order selling plan lets it be purchased
   * anyway, so we force the button enabled and relabel it. We only ever force the
   * pre-order state — when it's NOT pre-order we leave the button to the theme,
   * which manages availability/label itself (incl. via its variant-change morph).
   * @param {boolean} isPreorder
   */
  applyButtonState(isPreorder) {
    this.#forcePreorderButton(isPreorder);
    // The dynamic "Buy it now" (Shop Pay) button doesn't carry the pre-order
    // selling plan and reads oddly for a deposit, so hide it during pre-order.
    this.#toggleAcceleratedCheckout(isPreorder);
    // Show the pre-order panel (ETA + FAQ) only while pre-order is active.
    if (this.preorderPanel) this.preorderPanel.hidden = !isPreorder;
    // The theme's variant:update / variant:selected handlers may disable or morph
    // the button on the same tick. Re-assert after paint so pre-order wins.
    if (isPreorder) {
      requestAnimationFrame(() => {
        this.#forcePreorderButton(true);
        this.#toggleAcceleratedCheckout(true);
        this.updatePriceDisplay();
      });
    }
  }

  /**
   * Move the pre-order panel (ETA line + FAQ) out of the buy box and place it
   * directly after the product form's buy buttons, so it reads as "right below
   * the pre-order button." Falls back to leaving it in place if no form/buttons.
   */
  #relocatePreorderPanel() {
    this.preorderPanel = this.querySelector('.alloi-buy-box__preorder-panel');
    if (!this.preorderPanel) return;
    const scope = this.sectionEl instanceof HTMLElement ? this.sectionEl : document;
    // Theme-editor safety: a re-render creates a fresh panel, so drop any panel
    // left behind (relocated outside this element) by a previous render.
    scope.querySelectorAll('.alloi-buy-box__preorder-panel').forEach((p) => {
      if (p !== this.preorderPanel) p.remove();
    });
    // Place the FAQ panel below the trust row (so it sits under the button +
    // free-shipping line); fall back to right under the buy buttons.
    const anchor = scope.querySelector('.alloi-trust') || scope.querySelector('.buy-buttons-block');
    if (anchor) anchor.insertAdjacentElement('afterend', this.preorderPanel);
  }

  /**
   * Show/hide the accelerated checkout ("Buy it now") button. We only override
   * to HIDE for pre-order; passing false clears our override so the theme's own
   * availability logic governs it again.
   * @param {boolean} hide
   */
  #toggleAcceleratedCheckout(hide) {
    const form = this.getProductForm();
    if (!form) return;
    form.querySelectorAll('.accelerated-checkout-block').forEach((el) => {
      if (el instanceof HTMLElement) el.style.display = hide ? 'none' : '';
    });
  }

  /**
   * @param {boolean} isPreorder
   */
  #forcePreorderButton(isPreorder) {
    if (!isPreorder) return;
    this.getAddToCartButtons().forEach((button) => {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      const label =
        button.querySelector('.add-to-cart-text__content span') ||
        button.querySelector('.add-to-cart-text__content');
      if (label && label.textContent.trim() !== this.preorderLabel) {
        label.textContent = this.preorderLabel;
      }
    });

    // The theme hides the sticky bar via CSS while the variant is unavailable
    // (`sticky-add-to-cart:not([data-variant-available='true'])`). A pre-order is
    // purchasable, so flip the flag to let the bar show on scroll. Its button
    // puppets the (now-enabled) main button, so add-to-cart works from the bar.
    const sticky = (this.sectionEl || document).querySelector('sticky-add-to-cart');
    if (sticky) sticky.setAttribute('data-variant-available', 'true');
  }

  /**
   * Reflect the selected option's price on the host (for any external listener)
   * and into an optional `[data-alloi-buy-box-price]` slot if one exists.
   */
  updatePriceDisplay() {
    const selected = (this.activeGroup || this).querySelector('.alloi-buy-box__option.is-selected');
    if (!selected) return;
    this.dataset.selectedPrice = selected.dataset.price || '';

    // The card's `.alloi-buy-box__price` is the actual line total charged
    // (discounted plan price x bottle count); `.alloi-buy-box__compare` is the
    // struck-through original. Reuse the already-rendered money strings.
    const priceEl = selected.querySelector('.alloi-buy-box__price');
    if (!priceEl) return;
    const priceText = priceEl.textContent.trim();
    const compareEl = selected.querySelector('.alloi-buy-box__compare');
    const compareText = compareEl ? compareEl.textContent.trim() : '';

    const priceSlot = document.querySelector('[data-alloi-buy-box-price]');
    if (priceSlot) priceSlot.textContent = priceText;

    // Mirror into the sticky bar — discounted price + struck original when there
    // is a saving — reusing the buy box price/compare classes so it matches the
    // main box styling (incl. pre-order). Plain text when there's no compare-at.
    const stickyPrice = (this.sectionEl || document).querySelector('.sticky-add-to-cart__price');
    if (!stickyPrice) return;
    if (compareText) {
      stickyPrice.classList.add('alloi-buy-box__sticky-price');
      stickyPrice.innerHTML =
        '<span class="alloi-buy-box__price">' + priceText + '</span>' +
        '<s class="alloi-buy-box__compare">' + compareText + '</s>';
    } else {
      stickyPrice.classList.remove('alloi-buy-box__sticky-price');
      stickyPrice.textContent = priceText;
    }
  }

  /* ----------------------------------------------------------------------- *
   * Variant change
   * ----------------------------------------------------------------------- */

  /**
   * Swap the visible plan group when the variant changes. No fetch — every
   * variant's plans are already in the markup.
   * @param {CustomEvent} event
   */
  handleVariantUpdate(event) {
    const variant = event && event.detail ? event.detail.resource : null;
    if (!variant || variant.id == null) return;

    const next = this.querySelector(`.alloi-buy-box__group[data-variant-id="${variant.id}"]`);
    if (!next) return;

    this.querySelectorAll('.alloi-buy-box__group').forEach((group) => {
      group.toggleAttribute('hidden', group !== next);
    });

    this.activeGroup = next;
    this.bindGroup(next);
    this.selectDefault(next);
  }

  /* ----------------------------------------------------------------------- *
   * Collapsible (min / max)
   * ----------------------------------------------------------------------- */

  bindCollapseToggle() {
    if (this.dataset.collapsible !== 'true') return;
    this.collapseToggle = this.querySelector('.alloi-buy-box__collapse-toggle');
    if (this.collapseToggle) {
      this.collapseToggle.addEventListener('click', this.toggleCollapsed);
    }
  }

  toggleCollapsed() {
    const collapsed = this.getAttribute('data-collapsed') === 'true';
    if (collapsed) {
      this.removeAttribute('data-collapsed');
    } else {
      this.setAttribute('data-collapsed', 'true');
    }
    if (this.collapseToggle) {
      // aria-expanded is the inverse of collapsed; flip from the prior state.
      this.collapseToggle.setAttribute('aria-expanded', collapsed ? 'true' : 'false');
    }
  }

  /* ----------------------------------------------------------------------- *
   * Convenience cart method (SECONDARY path — see file header)
   * ----------------------------------------------------------------------- */

  /**
   * Add the variant to the cart via the AJAX API, including the selected plan.
   * @param {number|string} variantId
   * @param {number} [quantity=1]
   * @returns {Promise<object>}
   */
  addToCart(variantId, quantity) {
    const qty = quantity != null ? quantity : this.selectedQuantity || 1;
    const item = { id: variantId, quantity: qty };
    if (this.selectedPlanId) item.selling_plan = this.selectedPlanId;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item] }),
    }).then((response) => response.json());
  }
}

if (!customElements.get('alloi-buy-box')) {
  customElements.define('alloi-buy-box', AlloiBuyBox);
}
